import { useState } from "react";
import { Check } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";

const EU_COUNTRIES = [
  "Austria",
  "Belgium",
  "Bulgaria",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
];

// Danish translations for EU countries
const EU_COUNTRIES_DANISH = {
  "Austria": "Østrig",
  "Belgium": "Belgien",
  "Bulgaria": "Bulgarien",
  "Croatia": "Kroatien",
  "Cyprus": "Cypern",
  "Czech Republic": "Tjekkiet",
  "Denmark": "Danmark",
  "Estonia": "Estland",
  "Finland": "Finland",
  "France": "Frankrig",
  "Germany": "Tyskland",
  "Greece": "Grækenland",
  "Hungary": "Ungarn",
  "Ireland": "Irland",
  "Italy": "Italien",
  "Latvia": "Letland",
  "Lithuania": "Litauen",
  "Luxembourg": "Luxembourg",
  "Malta": "Malta",
  "Netherlands": "Holland",
  "Poland": "Polen",
  "Portugal": "Portugal",
  "Romania": "Rumænien",
  "Slovakia": "Slovakiet",
  "Slovenia": "Slovenien",
  "Spain": "Spanien",
  "Sweden": "Sverige",
};

const EU_EEA_COUNTRIES = [
  "Austria",
  "Belgium",
  "Bulgaria",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
  "Iceland",
  "Liechtenstein",
  "Norway",
];

// Danish translations for EU+EEA countries
const EU_EEA_COUNTRIES_DANISH = {
  "Austria": "Østrig",
  "Belgium": "Belgien",
  "Bulgaria": "Bulgarien",
  "Croatia": "Kroatien",
  "Cyprus": "Cypern",
  "Czech Republic": "Tjekkiet",
  "Denmark": "Danmark",
  "Estonia": "Estland",
  "Finland": "Finland",
  "France": "Frankrig",
  "Germany": "Tyskland",
  "Greece": "Grækenland",
  "Hungary": "Ungarn",
  "Ireland": "Irland",
  "Italy": "Italien",
  "Latvia": "Letland",
  "Lithuania": "Litauen",
  "Luxembourg": "Luxembourg",
  "Malta": "Malta",
  "Netherlands": "Holland",
  "Poland": "Polen",
  "Portugal": "Portugal",
  "Romania": "Rumænien",
  "Slovakia": "Slovakiet",
  "Slovenia": "Slovenien",
  "Spain": "Spanien",
  "Sweden": "Sverige",
  "Iceland": "Island",
  "Liechtenstein": "Liechtenstein",
  "Norway": "Norge"
};

interface CountrySelectProps {
  selectedCountries?: string[];
  onCountryChange: (country: string) => void;
}

export function CountrySelect({
  selectedCountries = [],
  onCountryChange,
}: CountrySelectProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const { i18n } = useTranslation();
  const isDanish = i18n.language === 'da';

  const filteredCountries = EU_EEA_COUNTRIES.filter((country) =>
    isDanish 
      ? EU_EEA_COUNTRIES_DANISH[country].toLowerCase().includes(searchQuery.toLowerCase())
      : country.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDisplayName = (country: string) => {
    return isDanish ? EU_EEA_COUNTRIES_DANISH[country] : country;
  };

  return (
    <div className="space-y-4">
      <input
        type="text"
        placeholder={isDanish ? "Søg lande..." : "Search countries..."}
        className="w-full px-3 py-2 border rounded-md"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedCountries.map((country) => (
          <Badge
            key={country}
            variant="secondary"
            className="cursor-pointer"
            onClick={() => onCountryChange(country)}
          >
            {getDisplayName(country)}
            <Check className="w-3 h-3 ml-1" />
          </Badge>
        ))}
      </div>

      <ScrollArea className="h-[200px] border rounded-md p-2">
        <div className="space-y-1">
          {filteredCountries.map((country) => (
            <div
              key={country}
              className={`px-2 py-1 rounded-sm cursor-pointer hover:bg-accent ${
                selectedCountries.includes(country)
                  ? "bg-accent"
                  : ""
              }`}
              onClick={() => onCountryChange(country)}
            >
              {getDisplayName(country)}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
